import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import { getWorklistsItemCount } from '../worklists/slice';
import { selectRisk } from '../risk/selectors';
import {
  getWorklistRisks,
  getWorklistRisksSucceeded,
  getWorklistRisksFailed,
  getWorklistRiskMessages,
  getWorklistRiskMessagesSucceeded,
  getWorklistRiskMessagesFailed,
  getWorklistRiskUnreadSince,
  getWorklistRiskUnreadSinceSucceeded,
  getWorklistRiskUnreadSinceFailed,
  deleteWorklistRiskUnreadSince,
  deleteWorklistRiskUnreadSinceSucceeded,
  deleteWorklistRiskUnreadSinceFailed,
  getRiskMessageRecipientOrganizations,
  getRiskMessageRecipientOrganizationsSucceeded,
  getRiskMessageRecipientOrganizationsFailed,
  createWorklistRiskMessage,
  createWorklistRiskMessageSucceeded,
  createWorklistRiskMessageFailed,
  createWorklistRiskMessageAndConnectedActions,
  createWorklistRiskMessageAndConnectedActionsSucceeded,
  createWorklistRiskMessageAndConnectedActionsFailed,
  updateWorklistRiskMessage,
  updateWorklistRiskMessageSucceeded,
  updateWorklistRiskMessageFailed,
  getWorklistRiskRecommendations,
  getWorklistRiskRecommendationsSucceeded,
  getWorklistRiskRecommendationsFailed,
  getWorklistRiskReminders,
  getWorklistRiskRemindersSucceeded,
  getWorklistRiskRemindersFailed,
  createWorklistRiskReminder,
  createWorklistRiskReminderSucceeded,
  createWorklistRiskReminderFailed,
  updateWorklistRiskReminder,
  updateWorklistRiskReminderSucceeded,
  updateWorklistRiskReminderFailed,
  deleteWorklistRiskReminder,
  deleteWorklistRiskReminderSucceeded,
  deleteWorklistRiskReminderFailed
} from './slice';

import {
  onGetWorklistRisks,
  onGetWorklistRiskMessages,
  onGetWorklistRiskUnreadSince,
  onDeleteWorklistRiskUnreadSince,
  onGetRiskMessageRecipientOrganizations,
  onCreateWorklistRiskMessage,
  onUpdateWorklistRiskMessage,
  onGetWorklistRiskRecommendations,
  onGetWorklistRiskReminders,
  onCreateWorklistRiskReminder,
  onUpdateWorklistRiskReminder,
  onDeleteWorklistRiskReminder
} from './api';

export function* getWorklistRisksWorker({ payload }) {
  const { params } = payload || {};

  try {
    const response = yield call(onGetWorklistRisks, params);
    yield put(getWorklistRisksSucceeded(response.data));
  } catch {
    yield put(getWorklistRisksFailed());
  }
}

export function* getWorklistRiskMessagesWorker({ payload }) {
  const { riskId } = payload;

  try {
    const response = yield call(onGetWorklistRiskMessages, riskId);
    yield put(getWorklistRiskMessagesSucceeded(response.data));
  } catch {
    yield put(getWorklistRiskMessagesFailed());
  }
}

export function* getWorklistRiskUnreadSinceWorker({ payload }) {
  const { riskId } = payload;

  try {
    const response = yield call(onGetWorklistRiskUnreadSince, riskId);
    yield put(getWorklistRiskUnreadSinceSucceeded(response.data.unread_since));
  } catch {
    yield put(getWorklistRiskUnreadSinceFailed());
  }
}

export function* deleteWorklistRiskUnreadSinceWorker({ payload }) {
  const { riskId } = payload;

  try {
    yield call(onDeleteWorklistRiskUnreadSince, riskId);
    yield put(deleteWorklistRiskUnreadSinceSucceeded());
    yield put(getWorklistsItemCount());
    yield put(getWorklistRiskUnreadSince({ riskId }));
  } catch {
    yield put(deleteWorklistRiskUnreadSinceFailed());
  }
}

export function* getMessageRecipientOrganizationsWorker({ payload }) {
  const { riskId } = payload;

  try {
    const response = yield call(onGetRiskMessageRecipientOrganizations, riskId);
    yield put(getRiskMessageRecipientOrganizationsSucceeded(response.data));
  } catch {
    yield put(getRiskMessageRecipientOrganizationsFailed());
  }
}

export function* createWorklistRiskMessageWorker({ payload }) {
  const { riskId, messageData, callback } = payload;

  try {
    yield call(onCreateWorklistRiskMessage, riskId, messageData);
    yield put(createWorklistRiskMessageSucceeded());
    callback?.();
  } catch {
    yield put(createWorklistRiskMessageFailed());
  }
}

export function* createWorklistRiskMessageAndConnectedActionsWorker({ payload }) {
  const { createReminder, riskId, newMessage, newReminder, callback } = payload;

  try {
    yield call(onCreateWorklistRiskMessage, riskId, newMessage);

    if (createReminder) {
      yield call(onCreateWorklistRiskReminder, riskId, newReminder);
    }

    yield put(createWorklistRiskMessageAndConnectedActionsSucceeded());
    callback?.();
  } catch {
    yield put(createWorklistRiskMessageAndConnectedActionsFailed());
  }
}

export function* updateWorklistRiskMessageWorker({ payload }) {
  const { message, callback } = payload;

  try {
    yield call(onUpdateWorklistRiskMessage, message);
    yield put(updateWorklistRiskMessageSucceeded());
    callback?.();
  } catch {
    yield put(updateWorklistRiskMessageFailed());
  }
}

export function* getWorklistRiskRecommendationsWorker({ payload }) {
  const { riskId } = payload;

  try {
    const response = yield call(onGetWorklistRiskRecommendations, riskId);
    yield put(getWorklistRiskRecommendationsSucceeded(response.data));
  } catch {
    yield put(getWorklistRiskRecommendationsFailed());
  }
}

export function* getWorklistRiskRemindersWorker({ payload }) {
  const { riskId } = payload;

  try {
    const response = yield call(onGetWorklistRiskReminders, riskId);
    yield put(getWorklistRiskRemindersSucceeded(response.data));
  } catch {
    yield put(getWorklistRiskRemindersFailed());
  }
}

export function* createWorklistRiskReminderWorker({ payload }) {
  const { riskId, reminder, callback } = payload;

  try {
    yield call(onCreateWorklistRiskReminder, riskId, reminder);
    yield put(createWorklistRiskReminderSucceeded());
    callback?.();
  } catch {
    yield put(createWorklistRiskReminderFailed());
  }
}

export function* updateWorklistRiskReminderWorker({ payload }) {
  const { reminderId, reminder, callback } = payload;

  try {
    yield call(onUpdateWorklistRiskReminder, reminderId, reminder);
    yield put(updateWorklistRiskReminderSucceeded());
    callback?.();
  } catch {
    yield put(updateWorklistRiskReminderFailed());
  }
}

export function* deleteWorklistRiskReminderWorker({ payload }) {
  const { reminderId, callback } = payload;

  try {
    yield call(onDeleteWorklistRiskReminder, reminderId);
    yield put(deleteWorklistRiskReminderSucceeded());
    yield put(getWorklistsItemCount());
    callback?.();
  } catch {
    yield put(deleteWorklistRiskReminderFailed());
  }
}

function* reloadWorklistRiskAllDataWorker() {
  const risk = yield select(selectRisk);
  const riskId = risk?.id;

  if (risk) {
    yield all([
      put(getWorklistRiskMessages({ riskId })),
      put(getWorklistRiskRecommendations({ riskId })),
      put(getWorklistRiskReminders({ riskId })),
      put(getWorklistRiskUnreadSince({ riskId }))
    ]);
    yield put(getWorklistsItemCount());
  }
}

export default function* worklistRisksSaga() {
  yield all([
    takeLatest(getWorklistRisks.type, getWorklistRisksWorker),
    takeLatest(getWorklistRiskMessages.type, getWorklistRiskMessagesWorker),
    takeLatest(getWorklistRiskUnreadSince.type, getWorklistRiskUnreadSinceWorker),
    takeLatest(getRiskMessageRecipientOrganizations.type, getMessageRecipientOrganizationsWorker),
    takeLatest(getWorklistRiskRecommendations.type, getWorklistRiskRecommendationsWorker),
    takeLatest(getWorklistRiskReminders.type, getWorklistRiskRemindersWorker),
    takeLatest(deleteWorklistRiskUnreadSince.type, deleteWorklistRiskUnreadSinceWorker),
    takeLatest(createWorklistRiskMessage.type, createWorklistRiskMessageWorker),
    takeLatest(createWorklistRiskMessageAndConnectedActions.type, createWorklistRiskMessageAndConnectedActionsWorker),
    takeLatest(updateWorklistRiskMessage.type, updateWorklistRiskMessageWorker),
    takeLatest(createWorklistRiskReminder.type, createWorklistRiskReminderWorker),
    takeLatest(updateWorklistRiskReminder.type, updateWorklistRiskReminderWorker),
    takeLatest(deleteWorklistRiskReminder.type, deleteWorklistRiskReminderWorker),
    takeEvery(createWorklistRiskMessageSucceeded.type, reloadWorklistRiskAllDataWorker),
    takeEvery(createWorklistRiskMessageAndConnectedActionsSucceeded.type, reloadWorklistRiskAllDataWorker),
    takeEvery(updateWorklistRiskMessageSucceeded.type, reloadWorklistRiskAllDataWorker),
    takeEvery(createWorklistRiskReminderSucceeded.type, reloadWorklistRiskAllDataWorker),
    takeEvery(updateWorklistRiskReminderSucceeded.type, reloadWorklistRiskAllDataWorker),
    takeEvery(deleteWorklistRiskReminderSucceeded.type, reloadWorklistRiskAllDataWorker)
  ]);
}
